import { getApiClient } from '../axios-config';

/**
 * Packages API Service
 * Handles all API calls related to packages
 */
class PackagesAPI {
  /**
   * Get packages for a project
   * @param {string} projectId - Project ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of package objects
   */
  static async getByProject(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/packages/project', {
      params: { projectId },
    });
    return response.data.data.packages || [];
  }

  /**
   * Get package options for a project (includes trackingStatuses and packageAreas)
   * @param {string} projectId - Project ID
   * @param {string} token - JWT authentication token
   * @param {string|null} packageId - Optional package ID to filter statuses by the package's status step set
   * @returns {Promise<Object>} Object with trackingStatuses and packageAreas
   */
  static async getPackageOptions(projectId, token, packageId = null) {
    const apiClient = getApiClient(token);
    const params = { projectId };
    if (packageId) {
      params.packageId = packageId;
    }
    const response = await apiClient.get('/api/packages/packageoptions', {
      params,
    });
    return response.data.data; // Returns { trackingStatuses, packageAreas, packages }
  }

  /**
   * Create a new package
   * @param {Object} packageData - Package object
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Created package object
   */
  static async create(packageData, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post('/api/packages/addpackage', {
      package: packageData,
    });
    const data = response.data.data;
    return {
      ...(data.addedPackage || packageData),
      accAssetSync: data.accAssetSync,
      accSyncFailed: data.accSyncFailed,
      accSyncError: data.accSyncError,
    };
  }

  /**
   * Update an existing package
   * @param {Object} packageData - Package object with updates
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated package object with optional autodeskSync and numTimecards
   */
  static async update(packageData, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.put('/api/packages/modifypackage', {
      updatedPackage: packageData,
    });
    // Return full data including modifiedPackage, autodeskSync, and numTimecards
    const data = response.data.data;
    return {
      ...data.modifiedPackage,
      autodeskSync: data.autodeskSync,
      numTimecards: data.numTimecards,
      accAssetSync: data.accAssetSync,
      accSyncFailed: data.accSyncFailed,
      accSyncError: data.accSyncError,
    };
  }

  /**
   * Soft-delete a package; its ACC asset, if any, is archived first (ticket #268).
   * Rejects, changing nothing, when the package has tracked time or budgeted
   * hours, or when its ACC asset could not be deleted.
   * @param {string} id - Package ID to delete
   * @param {string} token - JWT authentication token
   * @returns {Promise<{deletedPackage: Object, accSyncResult: Object}>}
   */
  static async delete(id, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.delete('/api/packages/deletepackage', {
      params: {
        packageId: id,
      },
    });
    return response.data.data;
  }

  /**
   * Soft-delete several packages (ticket #268). Each one goes through the same
   * checks as delete(); a blocked package does not stop the rest.
   * @param {string[]} packageIds - Package IDs to delete
   * @param {string} token - JWT authentication token
   * @returns {Promise<{deleted: Array<{id, name}>, blocked: Array<{id, reason}>}>}
   */
  static async bulkDelete(packageIds, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post('/api/packages/bulk-delete', {
      packageIds,
    });
    return response.data.data;
  }
  /**
   * Get active packages for timecard entry, filtered by employee location
   * Shop employees only see packages with requires_shop_fabrication = true
   * Field employees see all active packages
   * @param {string} employeeId - Employee ID to filter packages for (optional)
   * @param {string} token - JWT authentication token
   * @param {boolean} overrideToField - When true (shop-to-field override, ticket #246),
   *   a shop employee's list is further restricted to packages with populated
   *   field_phase/field_cost_code. Ignored for field employees. Defaults false.
   * @returns {Promise<Array>} Array of active package objects
   */
  static async getActivePackages(employeeId, token, overrideToField = false) {
    const apiClient = getApiClient(token);
    const params = employeeId ? { employeeId } : {};
    if (overrideToField) {
      params.overrideToField = true;
    }
    const response = await apiClient.get('/api/packages/active', { params });
    return response.data.data.packages || [];
  }

  /**
   * Validate packages CSV (dry run)
   * @param {string} projectId - Project ID
   * @param {FormData} formData - FormData containing csvFile
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Validation results containing preview rows and mode
   */
  static async validateCsv(projectId, formData, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/projects/${projectId}/packages/csv-validate`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  }

  /**
   * Commit packages CSV changes
   * @param {string} projectId - Project ID
   * @param {Object} payload - Object containing { mode, rows } and, when the
   *   upload changes budgeted hours, budget_change_reason
   *   ({ change_reason, change_order_reference, notes }) applied to every
   *   changed package (ticket #160)
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Commit summary (created, updated, skipped counts)
   */
  static async commitCsv(projectId, payload, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/projects/${projectId}/packages/csv-commit`,
      payload
    );
    return response.data.data;
  }

  /**
   * Progress of the background ACC asset run started by a CSV commit (ticket #257)
   * @param {string} projectId - Project ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { running, progress, counts: { pending, failed }, failures }
   */
  static async getAccAssetStatus(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/projects/${projectId}/packages/acc-assets/status`
    );
    return response.data.data;
  }

  /**
   * Queue the project's failed ACC assets again and start creating every
   * pending one in the background (ticket #257). 409 (error.reason
   * 'acc_asset_worker_running') while a run is already going.
   * @param {string} projectId - Project ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { started, requeued, reason?, running, progress, counts, failures }
   */
  static async retryAccAssets(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/projects/${projectId}/packages/acc-assets/retry`
    );
    return response.data.data;
  }

  /**
   * Download blank package template CSV
   * @param {string} projectId - Project ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Axios Response object containing file Blob
   */
  static async downloadCsvTemplate(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/projects/${projectId}/packages/csv-template`,
      {
        responseType: 'blob',
      }
    );
    return response;
  }

  /**
   * Download existing packages CSV
   * @param {string} projectId - Project ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Axios Response object containing file Blob
   */
  static async downloadPackagesCsv(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/projects/${projectId}/packages/csv-download`,
      {
        responseType: 'blob',
      }
    );
    return response;
  }

  /**
   * Update package budgeted hours
   * @param {string} packageId - Package ID
   * @param {Object} data - Update data { field_hrs_budgeted, shop_hrs_budgeted, change_reason, change_order_reference, notes }
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Success status and updated package/history info
   */
  static async updateBudget(packageId, data, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.put(
      `/api/packages/${packageId}/budget`,
      data
    );
    return response.data.data;
  }

  /**
   * Get budget change history for a package
   * @param {string} packageId - Package ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} History records
   */
  static async getBudgetHistory(packageId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/packages/${packageId}/budget-change-history`
    );
    return response.data.data.history || [];
  }

  /**
   * Manually unarchive a package
   * @param {string} packageId - Package ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} API response data
   */
  static async unarchive(packageId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.patch(
      `/api/packages/${packageId}/unarchive`
    );
    return response.data.data;
  }

  /**
   * Confirm Autodesk deletion
   * @param {string} packageId - Package ID
   * @param {string} token - JWT token
   * @param {string} notes - Admin justification notes
   * @returns {Promise<Object>} API response data
   */
  static async confirmAutodeskDeletion(packageId, token, notes) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/packages/${packageId}/confirm-autodesk-deletion`,
      { notes }
    );
    return response.data.data;
  }

  /**
   * Clear Autodesk deletion pending warning
   * @param {string} packageId - Package ID
   * @param {string} token - JWT token
   * @param {string} notes - Admin justification notes
   * @returns {Promise<Object>} API response data
   */
  static async clearAutodeskDeletionPending(packageId, token, notes) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/packages/${packageId}/clear-autodesk-deletion-pending`,
      { notes }
    );
    return response.data.data;
  }
}

export default PackagesAPI;
